import React, { useRef } from "react"
import slash from "../../../../../../assets/images/date-seperator.svg"

type DateInputValue = { dd: string; mm: string; yyyy: string }

type DateInputProps = {
  value?: DateInputValue
  onChange?: (val: DateInputValue) => void
  className?: string
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  className,
}) => {
  const ddRef = useRef<HTMLInputElement>(null)
  const mmRef = useRef<HTMLInputElement>(null)
  const yyyyRef = useRef<HTMLInputElement>(null)

  const handleChange =
    (
      key: "dd" | "mm" | "yyyy",
      nextRef?: React.RefObject<HTMLInputElement>,
      prevRef?: React.RefObject<HTMLInputElement>
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value.replace(/\D/g, "")
      if (key === "yyyy") val = val.slice(0, 4)
      else val = val.slice(0, 2)

      onChange?.({
        ...(value ?? { dd: "", mm: "", yyyy: "" }),
        [key]: val,
      } as DateInputValue)

      if (
        (key === "yyyy" && val.length === 4 && nextRef?.current) ||
        (key !== "yyyy" && val.length === 2 && nextRef?.current)
      ) {
        nextRef.current.focus()
      } else if (val.length === 0 && prevRef?.current) prevRef.current.focus()
    }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "")
    if (paste.length === 8) {
      // ddmmyyyy
      onChange?.({
        dd: paste.slice(0, 2),
        mm: paste.slice(2, 4),
        yyyy: paste.slice(4, 8),
      })
    } else if (paste.length === 6) {
      // ddmmyy -> expand to 20yy
      const yyyy = paste.slice(4, 6)
      onChange?.({
        dd: paste.slice(0, 2),
        mm: paste.slice(2, 4),
        yyyy: `${yyyy}`,
      })
    }
    e.preventDefault()
  }

  const inputClasses =
    "w-9 p-1 py-2 border border-gray-300 rounded-md text-center focus:outline-none focus:ring-1 focus:ring-blue-400"

  return (
    <div className={`flex items-center gap-0.5 ${className}`}>
      <input
        ref={ddRef}
        value={value?.dd}
        onChange={handleChange("dd", mmRef)}
        onPaste={handlePaste}
        placeholder="DD"
        className={inputClasses}
      />
      <img
        src={slash}
        alt="slash-icon"
        className=" w-5 h-7"
      />
      <input
        ref={mmRef}
        value={value?.mm}
        onChange={handleChange("mm", yyyyRef, ddRef)}
        placeholder="MM"
        className={inputClasses}
      />
      <img
        src={slash}
        alt="slash-icon"
        className=" w-5 h-7"
      />
      <input
        ref={yyyyRef}
        value={value?.yyyy}
        onChange={handleChange("yyyy", undefined, mmRef)}
        placeholder="YYYY"
        className="w-14 p-1 py-2 border border-gray-300 rounded-md text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
      />
    </div>
  )
}

export default DateInput
export type { DateInputValue }
